import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getCookie, removeCookie, setCookie } from "./cookies";
import { settings } from "./settings";

// Extend AxiosRequestConfig to include retry flag
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  skipAuthRetry?: boolean;
}

// List of endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  settings.api.auth.login,
  settings.api.auth.register,
  settings.api.auth.refresh
];

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: Error) => void;
}> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

const api = axios.create({
  baseURL: settings.api.base,
  // withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10 * 60 * 1000, // 10 minutes default timeout
});

// Add request interceptor for token handling
api.interceptors.request.use(
  async (config) => {
    // Skip token handling for public endpoints
    if (config.url && PUBLIC_ENDPOINTS.includes(config.url)) {
      return config;
    }

    const token = getCookie(settings.cookies.access_token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const user = getCookie(settings.cookies.user) ? JSON.parse(getCookie(settings.cookies.user) || "{}") : null;
    const org = getCookie(settings.cookies.organization) ? JSON.parse(getCookie(settings.cookies.organization) || "{}") : null;

    // Add organization header if available
    if (org?.uid) {
      config.headers["x-org-id"] = org.uid;
    }

    // Add audit headers if user and org exist
    if (user?.uid && org?.uid) {
      config.headers["x-user-id"] = user.uid;
      config.headers["x-org-id"] = org.uid;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for token refresh and retry mechanism
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    if (!originalRequest || originalRequest.skipAuthRetry) {
      return Promise.reject(error);
    }

    // Handle 401 errors with retry mechanism
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for token refresh
        try {
          await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          return api(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refresh_token = getCookie(settings.cookies.refresh_token);
        if (!refresh_token) {
          throw new Error("No refresh token available");
        }

        const response = await api.post(settings.api.auth.refresh, {
          refresh_token,
        }, {
          timeout: 10 * 60 * 1000, // 10 minutes timeout
        });

        if (response.data?.access_token) {
          setCookie(settings.cookies.access_token, response.data.access_token);
          originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
          processQueue();
          return api(originalRequest);
        } else {
          processQueue(new Error("Failed to refresh token"));
          throw new Error("Failed to refresh token");
        }
      } catch (error) {
        processQueue(error as Error);
        // Clear auth state and redirect
        removeCookie(settings.cookies.access_token);
        removeCookie(settings.cookies.refresh_token);
        removeCookie(settings.cookies.user);
        removeCookie(settings.cookies.organization);
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response) {
      switch (error.response.status) {
        case 403:
          console.error("Access forbidden:", error.response.data);
          break;
        case 404:
          console.error("Resource not found:", error.response.data);
          break;
        case 500:
          console.error("Server error:", error.response.data);
          break;
        default:
          console.error("API error:", error.response.data);
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Request setup error:", error.message);
    }

    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Clear cookies and redirect to login
          removeCookie(settings.cookies.access_token);
          removeCookie(settings.cookies.refresh_token);
          removeCookie(settings.cookies.user);
          removeCookie(settings.cookies.organization);
          window.location.href = "/login";
          break;
        case 403:
          console.error("Access forbidden:", error.response.data);
          break;
        case 404:
          console.error("Resource not found:", error.response.data);
          break;
        case 500:
          console.error("Server error:", error.response.data);
          break;
        default:
          console.error("API error:", error.response.data);
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Request setup error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
